import React, { useState, useEffect } from 'react';
import api, { uploadMedia } from '../utils/api';
import ImageCropperModal from './ImageCropperModal';
import './AddTeacherDialog.css';

const KNOWN_DESIGNATIONS = ['চেয়ারম্যান','অধ্যাপক','সহযোগী অধ্যাপক','সহকারী অধ্যাপক','প্রভাষক','সেকশন অফিসার','ডেপুটি রেজিস্টার','সহকারী রেজিস্টার','উপ পরিচালক','সহকারী পরিচালক','ল্যাব সহকারী','MLSS'];

const EditTeacherDialogGlobal = ({ teacher, isOpen, onClose, onSave, faculties, departments }) => {
  const [formData, setFormData] = useState({
    name: '',
    nameEn: '',
    email: '',
    facultyName: '',
    departmentId: '',
    dept_ref: '',
    designation: '',
    phoneNumber: '',
    dp: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [availableDepartments, setAvailableDepartments] = useState([]);
  const [isCustomDesignation, setIsCustomDesignation] = useState(false);
  const [customDesignationText, setCustomDesignationText] = useState('');
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    if (!selectedFile) { setPreviewUrl(null); return; }
    const url = URL.createObjectURL(selectedFile);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [selectedFile]);

  const fetchDepartmentsForFaculty = React.useCallback(async (facultyId, currentDeptId = null) => {
    if (!facultyId) {
      setAvailableDepartments([]);
      return;
    }

    try {
      const response = await api.get(`/departments?faculty_ref=${facultyId}`);
      if (response.data.success) {
        setAvailableDepartments(response.data.data);
        
        // If we have a current department ID, make sure it's set in formData
        if (currentDeptId && !formData.dept_ref) {
          setFormData(prev => ({
            ...prev,
            dept_ref: currentDeptId
          }));
        }
      }
    } catch (err) {
      console.error('Failed to fetch departments:', err);
      setAvailableDepartments([]);
    }
  }, [formData.dept_ref]);

  useEffect(() => {
    if (teacher && isOpen && faculties.length > 0) {
      const deptRefId = teacher.dept_ref?._id || teacher.dept_ref || '';
      const isCustom = !!teacher.designation && !KNOWN_DESIGNATIONS.includes(teacher.designation);
      setIsCustomDesignation(isCustom);
      setCustomDesignationText(isCustom ? teacher.designation : '');
      setFormData({
        name: teacher.name || '',
        nameEn: teacher.nameEn || '',
        email: teacher.email || '',
        facultyName: teacher.facultyName || '',
        departmentId: teacher.departmentId || '',
        dept_ref: deptRefId,
        designation: isCustom ? 'custom' : (teacher.designation || ''),
        phoneNumber: teacher.phoneNumber || '',
        dp: teacher.dp || ''
      });
      setError('');
      setSelectedFile(null);
      setUploading(false);
      
      if (teacher.facultyName) {
        const selectedFaculty = faculties.find(f => f.name === teacher.facultyName);
        if (selectedFaculty) {
          fetchDepartmentsForFaculty(selectedFaculty._id, deptRefId);
        }
      }
    }
  }, [teacher, isOpen, faculties, fetchDepartmentsForFaculty]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // If faculty changes, fetch departments and clear department selection
    if (name === 'facultyName' && value && faculties.length > 0) {
      const selectedFaculty = faculties.find(f => f.name === value);
      if (selectedFaculty) {
        fetchDepartmentsForFaculty(selectedFaculty._id);
        setFormData(prev => ({
          ...prev,
          dept_ref: '',
          departmentId: ''
        }));
      }
    }

    if (name === 'designation') {
      if (value === 'custom') {
        setIsCustomDesignation(true);
      } else {
        setIsCustomDesignation(false);
        setCustomDesignationText('');
      }
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!formData.name.trim() || !formData.email.trim() || !formData.designation || !formData.phoneNumber.trim() || !formData.facultyName || !formData.dept_ref) {
      setError('Please fill in all required fields.');
      setLoading(false);
      return;
    }

    const actualDesignation = isCustomDesignation ? customDesignationText.trim() : formData.designation;
    if (!actualDesignation) {
      setError('Please enter a custom designation.');
      setLoading(false);
      return;
    }

    let dpUrl = formData.dp;
    if (selectedFile) {
      const uploaded = await uploadSelectedFile();
      if (uploaded === null) { setLoading(false); return; }
      dpUrl = uploaded;
    }

    const submitData = {
      name: formData.name,
      nameEn: formData.nameEn,
      email: formData.email,
      facultyName: formData.facultyName,
      departmentId: formData.departmentId || 1,
      dept_ref: formData.dept_ref,
      designation: actualDesignation,
      phoneNumber: formData.phoneNumber,
      dp: dpUrl || null,
      priority: teacher.priority || 1
    };

    try {
      const response = await api.put(`/teachers/${teacher._id}`, submitData);
      if (response.data.success) {
        onSave(response.data.data);
        onClose();
      } else {
        setError(response.data.message || 'Failed to update teacher');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update teacher');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { setError('Please select an image file.'); return; }
    if (file.size > 5 * 1024 * 1024) { setError('File size must be less than 5MB.'); return; }
    setSelectedFile(file);
    setError('');
    setShowCropper(true);
  };

  const handleCropComplete = (croppedBlob) => {
    setShowCropper(false);
    const croppedFile = new File([croppedBlob], 'cropped-image.jpg', { type: 'image/jpeg' });
    setSelectedFile(croppedFile);
    const reader = new FileReader();
    reader.onload = (e) => setFormData(prev => ({ ...prev, dp: e.target.result }));
    reader.readAsDataURL(croppedBlob);
  };

  const handleRemoveDP = () => {
    setFormData(prev => ({ ...prev, dp: '' }));
    setSelectedFile(null);
    const fileInput = document.getElementById('dp-upload-global-edit');
    if (fileInput) fileInput.value = '';
  };

  const uploadSelectedFile = async () => {
    if (!selectedFile) return null;
    setUploading(true);
    try {
      const result = await uploadMedia(selectedFile);
      if (result.success) return result.url;
      setError('Upload failed: ' + (result.error || 'Unknown error'));
      return null;
    } catch (err) {
      setError('Upload failed: ' + err.message);
      return null;
    } finally {
      setUploading(false);
    }
  };

  if (!isOpen || !teacher) {
    return null;
  }

  return (
    <div className="dialog-overlay" onClick={handleClose}>
      <div className="dialog-content" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h2>Edit Teacher</h2>
          <button className="close-button" onClick={handleClose}>&times;</button>
        </div>

        <form onSubmit={handleSubmit} className="dialog-form">
          {error && <div className="error-message">{error}</div>}
          
          <div className="form-group">
            <label htmlFor="name">Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="Enter teacher name"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="nameEn">English Name</label>
            <input
              type="text"
              id="nameEn"
              name="nameEn"
              value={formData.nameEn}
              onChange={handleChange}
              placeholder="Enter teacher's English name"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="Enter email address"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="facultyName">Faculty *</label>
            <select
              id="facultyName"
              name="facultyName"
              value={formData.facultyName}
              onChange={handleChange}
              required
              disabled={loading}
            >
              <option value="">Select faculty</option>
              {faculties.map((faculty) => (
                <option key={faculty._id} value={faculty.name}>
                  {faculty.name}
                </option>
              ))}
            </select>
          </div>

          {formData.facultyName && (
            <div className="form-group">
              <label htmlFor="dept_ref">Department *</label>
              <select
                id="dept_ref"
                name="dept_ref"
                value={formData.dept_ref}
                onChange={handleChange}
                required
                disabled={loading}
              >
                <option value="">Select department</option>
                {availableDepartments.map((dept) => (
                  <option key={dept._id} value={dept._id}>
                    {dept.name}
                  </option>
                ))}
              </select>
              {formData.dept_ref && (
                <small style={{ color: '#666', fontSize: '12px' }}>
                  Current: {availableDepartments.find(d => d._id === formData.dept_ref)?.name || 
                    (teacher.dept_ref?.name || 'Unknown')}
                </small>
              )}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="designation">Designation *</label>
            <select
              id="designation"
              name="designation"
              value={formData.designation}
              onChange={handleChange}
              required
              disabled={loading}
            >
              <option value="">Select designation</option>
              <option value="চেয়ারম্যান">চেয়ারম্যান</option>
              <option value="অধ্যাপক">অধ্যাপক</option>
              <option value="সহযোগী অধ্যাপক">সহযোগী অধ্যাপক</option>
              <option value="সহকারী অধ্যাপক">সহকারী অধ্যাপক</option>
              <option value="প্রভাষক">প্রভাষক</option>
              <option value="সেকশন অফিসার">সেকশন অফিসার</option>
              <option value="ডেপুটি রেজিস্টার">ডেপুটি রেজিস্টার</option>
              <option value="সহকারী রেজিস্টার">সহকারী রেজিস্টার</option>
              <option value="উপ পরিচালক">উপ পরিচালক</option>
              <option value="সহকারী পরিচালক">সহকারী পরিচালক</option>
              <option value="ল্যাব সহকারী">ল্যাব সহকারী</option>
              <option value="MLSS">MLSS</option>
              <option value="custom">Custom</option>
            </select>
          </div>

          {isCustomDesignation && (
            <div className="form-group">
              <label htmlFor="customDesignation">Custom Designation *</label>
              <input
                type="text"
                id="customDesignation"
                name="customDesignation"
                placeholder="Enter custom designation"
                value={customDesignationText}
                disabled={loading}
                onChange={(e) => setCustomDesignationText(e.target.value)}
              />
            </div>
          )}

          <div className="form-group">
            <label htmlFor="phoneNumber">Phone Number *</label>
            <input
              type="tel"
              id="phoneNumber"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleChange}
              required
              placeholder="Enter phone number (e.g., +8801234567890)"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="dp-upload-global-edit">Profile Picture</label>
            <div className="avatar-upload-container">
              <div
                className={`circular-avatar ${selectedFile || formData.dp ? 'has-content' : ''}`}
                onClick={() => !loading && !uploading && document.getElementById('dp-upload-global-edit').click()}
              >
                {formData.dp ? (
                  <img src={formData.dp} alt="Profile" className="avatar-image" loading="lazy" decoding="async" />
                ) : previewUrl ? (
                  <img src={previewUrl} alt="Profile" className="avatar-image preview" />
                ) : (
                  <div className="avatar-placeholder">
                    <span className="avatar-icon">📷</span>
                    <span className="avatar-text">Click to upload</span>
                  </div>
                )}
              </div>
              <input
                type="file"
                id="dp-upload-global-edit"
                accept="image/*"
                onChange={handleFileSelect}
                disabled={loading || uploading}
                className="file-input-hidden"
                style={{ display: 'none' }}
              />
              {(selectedFile || formData.dp) && (
                <div className="avatar-actions">
                  {formData.dp && (
                    <button type="button" onClick={handleRemoveDP} className="remove-button" disabled={loading || uploading}>
                      Remove
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="dialog-actions">
            <button type="button" onClick={handleClose} className="cancel-button" disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="save-button" disabled={loading || uploading}>
              {loading ? 'Saving...' : 'Update Teacher'}
            </button>
          </div>
        </form>

        {showCropper && selectedFile && (
          <ImageCropperModal
            imageFile={selectedFile}
            onCropComplete={handleCropComplete}
            onCancel={() => { setShowCropper(false); setSelectedFile(null); }}
          />
        )}
      </div>
    </div>
  );
};

export default EditTeacherDialogGlobal;
